import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ArrowUp, ArrowDown, CalendarDays } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Checkbox,
  Button,
} from '@roger/ui-components';
import NotesDialog from './NotesDialog';
import TaskDocumentsPopover from '../documents/TaskDocumentsPopover';
import { Loader2, AlertCircle } from 'lucide-react';
import { DateTime } from 'luxon';
import { getOrderManagerItems } from '../../services/api';
import type { OrderManagerItemResponse } from '../../services/api/types.gen';
import {
  formatDateInAgencyZone,
  formatDateToZeroPaddedMMDDYYYY,
} from '../../utils/dateUtils';
import { formatOrderDeliveryPreference } from '../../utils/orderDeliveryPreference';
import { orderRowDestination, patientChartPath } from '../../utils/navigation';
import { ReportLink } from '../reporting/ReportLink';
import OrderManagerActions from './OrderManagerActions';
import EditDatesModal from './EditDatesModal';

type OrderManagerTabId = 'to-be-sent' | 'pending-signature' | 'received';

type SortField =
  | 'patient_name'
  | 'order_type'
  | 'order_date'
  | 'order_physician_name'
  | 'sent_to_physician_date'
  | 'received_date';
type SortDir = 'asc' | 'desc';

const STATUS_MAP: Record<OrderManagerTabId, string> = {
  'to-be-sent': 'completed',
  'pending-signature': 'sent_to_physician',
  received: 'returned_with_signature',
};

// Soft, informational highlight for a deep-linked order row (a neutral purple
// tint, not the red validation/error ring). Applied to the row, then faded out.
const ORDER_HIGHLIGHT_CLASSES = ['bg-violet-100', 'transition-colors'];
const ORDER_HIGHLIGHT_DURATION_MS = 2000;

// Whole days between a UTC datetime and now. Only valid for backend
// "timestamp with time zone" fields, which are emitted as UTC ISO strings.
function daysSince(utcDateTime: string | null | undefined): string {
  if (!utcDateTime) return '-';
  const dt = DateTime.fromISO(utcDateTime, { zone: 'utc' });
  if (!dt.isValid) return '-';
  const days = Math.ceil((Date.now() - dt.toMillis()) / (1000 * 60 * 60 * 24));
  return `${days}d`;
}

interface OrderManagerTabProps {
  tabId: OrderManagerTabId;
  refetchKey?: number;
  onAfterRefetch?: () => void;
  highlightOrderId?: string;
  hideImportedPatients?: boolean;
}

const OrderManagerTab: React.FC<OrderManagerTabProps> = ({
  tabId,
  refetchKey,
  onAfterRefetch,
  highlightOrderId,
  hideImportedPatients = true,
}) => {
  const onAfterRefetchRef = useRef(onAfterRefetch);
  onAfterRefetchRef.current = onAfterRefetch;
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderManagerItemResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('order_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [editNotesOrder, setEditNotesOrder] =
    useState<OrderManagerItemResponse | null>(null);
  const [editDatesOrder, setEditDatesOrder] =
    useState<OrderManagerItemResponse | null>(null);

  const highlightedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightOrderId || loading) return;
    if (highlightedRef.current === highlightOrderId) return; // one-shot
    if (!orders.some(o => o.id === highlightOrderId)) return; // not in this tab
    const el = document.querySelector<HTMLElement>(
      `[data-order-id="${highlightOrderId}"]`
    );
    if (!el) return;
    highlightedRef.current = highlightOrderId;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add(...ORDER_HIGHLIGHT_CLASSES);
    const t = window.setTimeout(
      () => el.classList.remove('bg-violet-100'),
      ORDER_HIGHLIGHT_DURATION_MS
    );
    return () => window.clearTimeout(t);
  }, [highlightOrderId, loading, orders]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getOrderManagerItems({
        query: {
          status: STATUS_MAP[tabId],
          hide_imported_patients: hideImportedPatients,
        },
      });
      setOrders(data ?? []);
      onAfterRefetchRef.current?.();
    } catch {
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [hideImportedPatients, tabId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, refetchKey]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(orders.map(o => o.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(orderId);
      else next.delete(orderId);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ArrowUp className='ml-1 inline h-3 w-3' />
    ) : (
      <ArrowDown className='ml-1 inline h-3 w-3' />
    );
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const valA = a[sortField] ?? '';
    const valB = b[sortField] ?? '';
    const cmp = String(valA).localeCompare(String(valB));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const showSentColumns = tabId === 'pending-signature' || tabId === 'received';
  const showAgeColumn = tabId === 'pending-signature';
  const showReceivedColumn = tabId === 'received';
  const selectedOrders = orders.filter(order => selectedIds.has(order.id));

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center gap-2 py-12 text-destructive'>
        <AlertCircle className='h-5 w-5' />
        <span>{error}</span>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className='py-12 text-center text-muted-foreground'>
        No orders in this stage
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='w-10'>
              <Checkbox
                checked={
                  selectedIds.size === orders.length && orders.length > 0
                }
                onCheckedChange={handleSelectAll}
              />
            </TableHead>
            <TableHead>Order #</TableHead>
            <TableHead
              className='cursor-pointer select-none'
              onClick={() => handleSort('patient_name')}
            >
              Patient <SortIcon field='patient_name' />
            </TableHead>
            <TableHead
              className='cursor-pointer select-none'
              onClick={() => handleSort('order_type')}
            >
              Type <SortIcon field='order_type' />
            </TableHead>
            <TableHead
              className='cursor-pointer select-none'
              onClick={() => handleSort('order_physician_name')}
            >
              Physician <SortIcon field='order_physician_name' />
            </TableHead>
            <TableHead>Delivery Pref.</TableHead>
            <TableHead
              className='cursor-pointer select-none'
              onClick={() => handleSort('order_date')}
            >
              Order Date <SortIcon field='order_date' />
            </TableHead>
            {showSentColumns && (
              <>
                <TableHead
                  className='cursor-pointer select-none'
                  onClick={() => handleSort('sent_to_physician_date')}
                >
                  Sent Date <SortIcon field='sent_to_physician_date' />
                </TableHead>
                {showAgeColumn && <TableHead>Age</TableHead>}
              </>
            )}
            {showReceivedColumn && <TableHead>Received</TableHead>}
            {showReceivedColumn && <TableHead>Phys. Sign Date</TableHead>}
            <TableHead className='w-10'>Docs</TableHead>
            <TableHead className='w-10'>Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedOrders.map(order => (
            <TableRow
              key={order.id}
              data-order-id={order.id}
              className='cursor-pointer'
              onClick={() => navigate(orderRowDestination(order))}
            >
              <TableCell onClick={e => e.stopPropagation()}>
                <Checkbox
                  checked={selectedIds.has(order.id)}
                  onCheckedChange={(checked: boolean) =>
                    handleSelectOrder(order.id, !!checked)
                  }
                />
              </TableCell>
              <TableCell>{order.id.slice(0, 8)}</TableCell>
              <TableCell>
                <ReportLink to={patientChartPath(order.patient_id)}>
                  {order.patient_name}
                </ReportLink>
              </TableCell>
              <TableCell>{order.order_type_display_name}</TableCell>
              <TableCell>{order.order_physician_name}</TableCell>
              <TableCell>
                {formatOrderDeliveryPreference(
                  order.physician_delivery_preference
                ) ?? '—'}
              </TableCell>
              <TableCell>
                {formatDateToZeroPaddedMMDDYYYY(order.order_date) || '-'}
              </TableCell>
              {showSentColumns && (
                <>
                  <TableCell>
                    {formatDateInAgencyZone(order.sent_to_physician_date) ||
                      '-'}
                  </TableCell>
                  {showAgeColumn && (
                    <TableCell>
                      {daysSince(order.sent_to_physician_date)}
                    </TableCell>
                  )}
                </>
              )}
              {showReceivedColumn && (
                <TableCell>
                  {(order.signature_required &&
                    formatDateInAgencyZone(order.received_date)) ||
                    '—'}
                </TableCell>
              )}
              {showReceivedColumn && (
                <TableCell>
                  {(order.signature_required &&
                    formatDateInAgencyZone(order.physician_sign_date)) ||
                    '—'}
                </TableCell>
              )}
              <TableCell onClick={e => e.stopPropagation()}>
                <TaskDocumentsPopover
                  taskId={order.id}
                  patientId={order.patient_id}
                  initialCount={order.document_count}
                  targetLabel={`${order.order_type_display_name} · ${order.patient_name}`}
                  onDocumentsChanged={fetchOrders}
                />
              </TableCell>
              <TableCell onClick={e => e.stopPropagation()}>
                <div className='flex items-center gap-1'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    aria-label={`Edit notes for ${order.order_type_display_name}`}
                    onClick={() => setEditNotesOrder(order)}
                  >
                    <MessageSquare
                      className={
                        order.notes
                          ? 'text-blue-500'
                          : 'text-muted-foreground/30'
                      }
                    />
                  </Button>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    aria-label={`Edit ${order.order_type_display_name}`}
                    onClick={() => setEditDatesOrder(order)}
                  >
                    <CalendarDays className='text-muted-foreground/50' />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <OrderManagerActions
        activeTab={tabId}
        selectedOrders={selectedOrders}
        onClearSelection={() => setSelectedIds(new Set())}
        onActionComplete={fetchOrders}
      />

      <NotesDialog
        open={!!editNotesOrder}
        onOpenChange={open => {
          if (!open) setEditNotesOrder(null);
        }}
        taskIds={editNotesOrder ? [editNotesOrder.id] : []}
        initialNotes={editNotesOrder?.notes ?? ''}
        mode='single'
        onSuccess={fetchOrders}
      />

      {editDatesOrder && (
        <EditDatesModal
          open={!!editDatesOrder}
          onOpenChange={open => {
            if (!open) setEditDatesOrder(null);
          }}
          orders={[editDatesOrder]}
          activeTab={tabId}
          onSuccess={fetchOrders}
        />
      )}
    </>
  );
};

export default OrderManagerTab;
